import React, { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Spinner } from "react-bootstrap";
import { Save } from "lucide-react";
import AssignmentMetaForm from "./AssignmentMetaForm";
import AssignmentQuestionPicker from "./AssignmentQuestionPicker";
import TagSelector from "../CodingQuestionBank/TagSelector";
import { AppCard, AppCardHeader, AppCardTitle, AppCardContent, AppCardFooter, AppButton, FormActionBar, ResetButton } from "../ui";
import apiClient from "../../utils/apiClient";
import { fetchJudgeLanguages, toCategoryOptions } from "../../utils/judgeLanguages";
import { toDateTimeLocal, fromDateTimeLocal } from "../../utils/datetime";

const initialMeta = {
  title: "",
  description: "",
  batch: "",
  startDate: "",
  endDate: "",
  timeLimit: "",
  assignmentType: "practice",
  numQuestions: 1,
  lockedLanguages: [],
};

const AssignmentForm = ({ onSuccess, assignment, editMode }) => {
  const navigate = useNavigate();
  const [meta, setMeta] = useState(initialMeta);
  const [loading, setLoading] = useState(false);
  const [batches, setBatches] = useState([]);
  const [selectedQuestions, setSelectedQuestions] = useState([]);
  const [error, setError] = useState("");

  // Language-lock: options are the judge0 tech-stacks (same list QuestionForm
  // uses for a question's "categories"). `restrictLangs` is the toggle; when
  // off we persist an empty `lockedLanguages` = no restriction.
  const [languageOptions, setLanguageOptions] = useState([]);
  const [loadingLangs, setLoadingLangs] = useState(true);
  const [restrictLangs, setRestrictLangs] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    try {
      const payload = {
        ...meta,
        // datetime-local values are local wall-clock — send absolute instants so
        // the server's timezone can't shift the schedule.
        startDate: fromDateTimeLocal(meta.startDate),
        endDate: fromDateTimeLocal(meta.endDate),
        questions: selectedQuestions,
      };
      if (assignment && assignment._id) {
        await apiClient.put(`/assignments/${assignment._id}`, payload);
      } else {
        await apiClient.post("/assignments/add", payload);
      }
      onSuccess();
    } catch (err) {
      setError(err?.response?.data?.message || "Failed to save assignment. Please try again.");
    }
    setLoading(false);
  };

  // Hydrate from the assignment being edited (or clear back to defaults when
  // adding). Reused by the Reset action to restore the pristine state.
  const hydrate = useCallback(() => {
    if (assignment) {
      setMeta({
        title: assignment.title,
        description: assignment.description,
        batch: assignment.batch?._id || assignment.batch,
        startDate: toDateTimeLocal(assignment.startDate),
        endDate: toDateTimeLocal(assignment.endDate),
        timeLimit: assignment.timeLimit,
        assignmentType: assignment.assignmentType || "practice",
        numQuestions: assignment.numQuestions,
        lockedLanguages: assignment.lockedLanguages || [],
      });
      setRestrictLangs((assignment.lockedLanguages || []).length > 0);
      setSelectedQuestions(assignment.questions?.map(q => q._id || q) || []);
    } else {
      setMeta(initialMeta);
      setRestrictLangs(false);
      setSelectedQuestions([]);
    }
  }, [assignment]);

  useEffect(() => { hydrate(); }, [hydrate]);

  const handleReset = () => {
    hydrate();
    setError("");
  };

  useEffect(() => {
    apiClient.get("/batches/list")
      .then(res => setBatches(res.data ? res.data.batches : []))
      .catch(() => setBatches([]));
  }, []);

  useEffect(() => {
    fetchJudgeLanguages()
      .then(raw => setLanguageOptions(toCategoryOptions(raw)))
      .catch(() => setLanguageOptions([]))
      .finally(() => setLoadingLangs(false));
  }, []);

  const handleRestrictToggle = (checked) => {
    setRestrictLangs(checked);
    // Clearing the list when turning the lock off keeps the payload honest:
    // an empty `lockedLanguages` is what the BE/student reads as "no limit".
    if (!checked) setMeta(m => ({ ...m, lockedLanguages: [] }));
  };

  const canSubmit =
    meta.title &&
    meta.batch &&
    selectedQuestions.length >= (meta.numQuestions || 1) &&
    (!restrictLangs || meta.lockedLanguages.length > 0);

  return (
    <form className="asgn-form has-form-actionbar" onSubmit={handleSubmit}>
      {error && <div className="alert alert-danger py-2 mb-3 small">{error}</div>}
      {/* Assignment Details */}
      <AppCard>
        <AppCardHeader>
          <AppCardTitle>Assignment Details</AppCardTitle>
        </AppCardHeader>
        <AppCardContent>
          <AssignmentMetaForm meta={meta} setMeta={setMeta} batches={batches} />
        </AppCardContent>
      </AppCard>

      {/* Language Restriction */}
      <AppCard>
        <AppCardHeader>
          <AppCardTitle>Language Restriction</AppCardTitle>
        </AppCardHeader>
        <AppCardContent>
          <div className="form-check form-switch mb-2">
            <input
              className="form-check-input"
              type="checkbox"
              role="switch"
              id="asgn-lock-langs"
              checked={restrictLangs}
              onChange={e => handleRestrictToggle(e.target.checked)}
            />
            <label className="form-check-label" htmlFor="asgn-lock-langs">
              Lock students to specific coding languages
            </label>
          </div>
          <span className="asgn-form__hint">
            When enabled, students can only choose and submit in the languages you select below. Leave off to allow every language.
          </span>
          {restrictLangs && (
            <div className="mt-3">
              {loadingLangs ? (
                <div className="d-flex align-items-center gap-2">
                  <Spinner size="sm" animation="border" />
                  <span className="text-muted small">Loading languages…</span>
                </div>
              ) : (
                <>
                  <TagSelector
                    options={languageOptions}
                    value={meta.lockedLanguages}
                    onChange={vals => setMeta(m => ({ ...m, lockedLanguages: vals }))}
                    label="Allowed languages"
                    placeholder="Select languages students may use"
                  />
                  {!meta.lockedLanguages.length && (
                    <span className="asgn-form__hint">Select at least one language to continue.</span>
                  )}
                </>
              )}
            </div>
          )}
        </AppCardContent>
      </AppCard>

      {/* Question Selection */}
      <AppCard>
        <AppCardHeader>
          <AppCardTitle>Question Pool</AppCardTitle>
        </AppCardHeader>
        <AppCardContent>
          <AssignmentQuestionPicker
            selectedQuestions={selectedQuestions}
            setSelectedQuestions={setSelectedQuestions}
          />
        </AppCardContent>
        <AppCardFooter>
          <div className="asgn-form__footer">
            <div className="asgn-form__field">
              <label className="asgn-form__label">Questions per student</label>
              <input
                className="asgn-form__input asgn-form__input--sm"
                type="number"
                min={1}
                max={selectedQuestions.length || 1}
                value={meta.numQuestions === 0 ? "" : meta.numQuestions}
                onChange={e => {
                  const val = e.target.value;
                  if (val === "") {
                    setMeta(m => ({ ...m, numQuestions: "" }));
                  } else {
                    const num = Number(val.replace(/^0+/, ""));
                    setMeta(m => ({ ...m, numQuestions: isNaN(num) ? "" : num }));
                  }
                }}
                required
              />
              <span className="asgn-form__hint">Each student will get this many randomly chosen questions.</span>
            </div>
          </div>
        </AppCardFooter>
      </AppCard>

      <FormActionBar>
        <AppButton type="button" variant="outline" onClick={() => navigate(-1)}>
          Cancel
        </AppButton>
        <ResetButton onReset={handleReset} />
        <AppButton type="submit" disabled={!canSubmit || loading}>
          <Save size={16} />
          {loading ? "Saving…" : editMode ? "Update Assignment" : "Create Assignment"}
        </AppButton>
      </FormActionBar>
    </form>
  );
};

export default AssignmentForm;
